import { useEffect, useState } from 'react';
import { Avatar } from './ProfileParts';

/* Performance from appointments: one board per job, each judged only on what that person controls.

   Sales reps   → once they sat down with the owners, did they sell? No shows / no opps / NRAs are
                  NOT the rep's fault, so reps are compared on revenue per sit-down, not raw totals.
   Setters      → (telemarketers and canvassers) when a rep drove out, were the owners home?
   Confirmers   → of the appointments they confirmed, were the owners home? Cancelling before
                  the rep drives out is a trip saved, not a failure.

   Owners home  = sat down (sold, pitched no sale, no demo) ÷ trips (sat down + no show / no opp + NRA)
   "No result entered" is left out of every rate. When two people share an appointment, each gets the
   appointment and an equal share of its revenue.                                                     */

const money = v => '$' + Math.round(Number(v) || 0).toLocaleString('en-US');
const pctN = (a, b) => (b ? Math.round(a / b * 100) : null);
const pctT = (a, b) => (b ? Math.round(a / b * 100) + '%' : '—');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

// field: 'reps' | 'setters' | null — whose share of the revenue to count
export function statsOf(list, field = null) {
  const c = { sold: 0, demo: 0, nodemo: 0, nra: 0, canc: 0, noshow: 0, pend: 0 };
  let revenue = 0;
  for (const a of list) {
    c[a.out]++;
    if (a.out === 'sold') revenue += (a.amount || 0) / (field ? Math.max(1, a[field].length) : 1);
  }
  const sat = c.sold + c.demo + c.nodemo, trips = sat + c.noshow + c.nra;
  return { ...c, set: list.length, sat, trips, revenue, wasted: c.noshow + c.nra,
    ownersHome: pctN(sat, trips), close: pctN(c.sold, c.sold + c.demo), sitSale: pctN(c.sold, sat),
    noDemo: pctN(c.nodemo, sat), perAppt: list.length ? revenue / list.length : 0, perSit: sat ? revenue / sat : 0 };
}

const VERDICT = {
  good: { label: 'Good', icon: '✓', fg: '#1F6B43', bg: '#E3F3EA', bar: '#2E9B5F' },
  watch: { label: 'Watch', icon: '!', fg: '#8A5A00', bg: '#FFF1D6', bar: '#E0A021' },
  bad: { label: 'Problem', icon: '✕', fg: '#A3322A', bg: '#FBE4E1', bar: '#D2453B' },
  few: { label: 'Too few to judge', icon: '·', fg: '#5E6E66', bg: '#EEF2EF', bar: '#B0B8B4' },
};
const ORDER = { bad: 0, watch: 1, good: 2, few: 3 };
// Setters & confirmers: owners-home rate vs the team, in points
export function homeVerdict(s, team) {
  if (s.trips < 5) return 'few';
  if (s.ownersHome >= team.ownersHome - 5) return 'good';
  if (s.ownersHome >= team.ownersHome - 15) return 'watch';
  return 'bad';
}
// Reps: sit-down → sale rate and revenue per sit-down vs the team.
// Good = sells 80% as often as the team OR makes at least the team's $ per sit-down; Problem = under half on both.
export function repVerdict(s, team) {
  if (s.sat < 5) return 'few';
  if (s.sitSale >= team.sitSale * 0.8 || s.perSit >= team.perSit) return 'good';
  if (s.sitSale < team.sitSale * 0.5 && s.perSit < team.perSit * 0.5) return 'bad';
  return 'watch';
}
export const Verdict = ({ v }) => { const x = VERDICT[v]; return (
  <span className="sc-v" style={{ color: x.fg, background: x.bg }}><b aria-hidden>{x.icon}</b>{x.label}</span>); };

const CSS = `
.sc-att{background:#fff;border:1px solid #D9E2DC;border-radius:16px;padding:18px 20px;margin-top:18px}
.sc-att h2{font-family:'Fraunces',Georgia,serif;font-size:20px;margin:0 0 12px}
.sc-items{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.sc-item{display:flex;gap:12px;align-items:flex-start;border-radius:12px;padding:13px 14px;border:1px solid #D9E2DC;cursor:pointer;text-align:left;font:inherit;background:#fff;color:#13211B}
.sc-item:hover{border-color:#0D3B2E}
.sc-item .ic{width:28px;height:28px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px}
.sc-item p{margin:0;font-size:14px;line-height:1.5}
.sc-item small{display:block;color:#5E6E66;font-size:12.5px;margin-top:3px}
.sc-v{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:700;padding:4px 10px;border-radius:999px;white-space:nowrap}
.sc-v b{font-size:12px}
.sc-tabs{display:flex;gap:4px;margin-top:22px;border-bottom:2px solid #D9E2DC;overflow-x:auto}
.sc-tab{font:inherit;font-size:14.5px;font-weight:700;color:#5E6E66;background:none;border:0;padding:11px 16px;cursor:pointer;border-bottom:3px solid transparent;margin-bottom:-2px;white-space:nowrap;display:inline-flex;gap:8px;align-items:center}
.sc-tab:hover{color:#13211B}
.sc-tab.on{color:#0D3B2E;border-bottom-color:#B8912E}
.sc-tab em{font-style:normal;font-size:12px;background:#EEF2EF;color:#5E6E66;border-radius:999px;padding:1px 8px}
.sc-tab.on em{background:#0D3B2E;color:#fff}
.sc-tab .dot{width:8px;height:8px;border-radius:50%;background:#D2453B}
.sc-q{background:#0D3B2E;color:#fff;border-radius:16px;padding:18px 22px;margin-top:18px;display:flex;gap:18px;align-items:center;flex-wrap:wrap}
.sc-q h2{font-family:'Fraunces',Georgia,serif;font-size:22px;margin:0 0 4px;color:#fff}
.sc-q p{margin:0;font-size:14px;line-height:1.55;color:rgba(255,255,255,.82);max-width:760px}
.sc-q .num{margin-left:auto;text-align:right}
.sc-q .num b{display:block;font-family:'Fraunces',Georgia,serif;font-size:34px;color:#f0c842;line-height:1}
.sc-q .num span{font-size:12.5px;color:rgba(255,255,255,.75)}
.sc-tbl td{padding:11px 8px}
.sc-tbl td:first-child b{white-space:nowrap}
.sc-who{display:flex;align-items:center;gap:10px;min-width:150px}
.sc-who b{display:block;line-height:1.2}
.sc-who small{color:#5E6E66;font-size:11.5px}
.sc-meter{position:relative;height:10px;border-radius:5px;background:#EEF2EF;min-width:110px}
.sc-meter i{position:absolute;left:0;top:0;bottom:0;border-radius:5px}
.sc-meter u{position:absolute;top:-4px;bottom:-4px;width:2px;background:#13211B;text-decoration:none}
.sc-big{font-weight:800;font-size:15px;font-variant-numeric:tabular-nums}
.sc-legend{display:flex;flex-wrap:wrap;gap:6px 16px;font-size:12.5px;color:#5E6E66;margin:0 0 12px}
.sc-legend span{display:inline-flex;align-items:center;gap:6px}
.sc-tick{width:2px;height:12px;background:#13211B;display:inline-block}
.sc-th2{font-size:10.5px;font-weight:600;color:#8A968F;display:block}
.sc-fair{display:grid;grid-template-columns:minmax(150px,200px) 1fr 1fr;gap:10px 18px;align-items:center}
.sc-fair .sc-mini-btn{font-weight:400;min-width:0}
.sc-fair .h{font-size:12px;font-weight:700;color:#5E6E66;border-bottom:1px solid #D9E2DC;padding-bottom:6px}
.sc-fbar{display:flex;align-items:center;gap:10px}
.sc-fbar i{display:block;height:16px;border-radius:4px;min-width:2px}
.sc-fbar span{font-size:13px;font-variant-numeric:tabular-nums;white-space:nowrap}
.sc-fbar small{color:#5E6E66;font-size:12px}
.sc-link{font:inherit;border:0;background:none;padding:0;color:#0D3B2E;font-weight:700;text-decoration:underline;cursor:pointer}
.sc-unsorted{background:#FFF8E6;border:1px solid #E8B04A;border-radius:14px;padding:14px 16px;margin-top:18px}
.sc-unsorted h3{margin:0 0 4px;font-size:15px}
.sc-unsorted p{margin:0 0 10px;font-size:13px;color:#5E6E66}
.sc-us-row{display:flex;flex-wrap:wrap;gap:8px}
.sc-us{display:inline-flex;align-items:center;gap:6px;background:#fff;border:1px solid #E6D4A6;border-radius:999px;padding:4px 5px 4px 12px;font-size:13px;font-weight:700}
.sc-us button,.sc-mini-btn{font:inherit;font-size:12px;font-weight:700;border:1.5px solid #0D3B2E;background:#fff;color:#0D3B2E;border-radius:999px;padding:3px 10px;cursor:pointer}
.sc-us button:hover,.sc-mini-btn:hover{background:#0D3B2E;color:#fff}
.sc-empty{background:#fff;border:2px dashed #D9E2DC;border-radius:16px;padding:30px 24px;text-align:center;color:#5E6E66;margin-top:18px;line-height:1.6}
.sc-panel-wrap{position:fixed;inset:0;z-index:1000;background:rgba(9,30,23,.42);display:flex;justify-content:flex-end}
.sc-panel{width:min(760px,100%);height:100%;background:#F7F9F8;overflow-y:auto;box-shadow:-24px 0 60px -30px rgba(0,0,0,.5);animation:scIn .24s cubic-bezier(.2,.8,.2,1)}
.sc-panel header{position:sticky;top:0;z-index:2;background:#0D3B2E;color:#fff;padding:20px 24px;display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
.sc-panel header h2{margin:0;font-family:'Fraunces',Georgia,serif;font-size:26px}
.sc-panel header .role{font-size:13px;opacity:.75}
.sc-panel header button{width:36px;height:36px;border-radius:50%;border:1px solid rgba(255,255,255,.4);background:transparent;color:#fff;cursor:pointer;flex-shrink:0}
.sc-pb{padding:18px 24px 30px}
.sc-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:16px}
.sc-kpi{background:#fff;border:1px solid #D9E2DC;border-radius:12px;padding:12px 14px}
.sc-kpi b{display:block;font-family:'Fraunces',Georgia,serif;font-size:24px}
.sc-kpi span{font-size:12.5px;color:#5E6E66}
.sc-say{background:#fff;border-left:4px solid #B8912E;border-radius:10px;padding:12px 14px;font-size:14.5px;line-height:1.55;margin-bottom:16px}
.sc-funnel{background:#fff;border:1px solid #D9E2DC;border-radius:12px;padding:14px 16px;margin-bottom:12px}
.sc-funnel h3{margin:0 0 10px;font-size:14px}
.sc-fn{display:grid;grid-template-columns:150px 1fr 54px;gap:10px;align-items:center;font-size:13.5px;margin-bottom:7px}
.sc-fn i{display:block;height:20px;border-radius:4px}
.sc-fn span:last-child{text-align:right;font-weight:800;font-variant-numeric:tabular-nums}
.sc-fn-note{font-size:12.5px;color:#5E6E66;margin:4px 0 0 160px;line-height:1.5}
.sc-grp{background:#fff;border:1px solid #D9E2DC;border-radius:12px;margin-bottom:12px;overflow:hidden}
.sc-grp h3{margin:0;padding:10px 14px;font-size:14px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #EEF2EF}
.sc-grp table{width:100%;border-collapse:collapse;font-size:13.5px}
.sc-grp td{padding:8px 14px;border-bottom:1px solid #F1F4F2;vertical-align:top}
.sc-ww td{padding:8px}
.sc-ww select{font:inherit;font-size:13px;padding:6px 8px;border:1px solid #D9E2DC;border-radius:8px;background:#fff;max-width:220px}
@keyframes scIn{from{transform:translateX(40px);opacity:.4}}
@media(max-width:1000px){.sc-items{grid-template-columns:1fr 1fr}}
@media(max-width:700px){.sc-fair .sc-who>img,.sc-fair .sc-who>div[aria-hidden]{display:none}.sc-fair .sc-who{min-width:0}.sc-items{grid-template-columns:1fr}.sc-kpis{grid-template-columns:1fr 1fr}.sc-fair{grid-template-columns:110px 1fr}.sc-fair .tot{display:none}.sc-fn{grid-template-columns:110px 1fr 44px}.sc-fn-note{margin-left:0}.sc-q .num{margin-left:0;text-align:left}}
@media(prefers-reduced-motion:reduce){.sc-panel{animation:none}}
`;
export const ScCss = () => <style>{CSS}</style>;

// ── People: Sales Gen short names ("Donna Mc") matched to staff profiles ──────
const low = s => String(s || '').trim().toLowerCase();
export function matchStaff(sgName, staff) {
  const n = low(sgName);
  if (!n) return null;
  const hits = staff.filter(s => {
    const full = low(s.name);
    if (full === n || low(s.nickname) === n || full.startsWith(n)) return true;
    const [f, ...rest] = n.split(/\s+/); const [sf, ...srest] = full.split(/\s+/);
    return rest.length && f === sf && srest.join(' ').startsWith(rest.join(' '));
  });
  return hits.length === 1 ? hits[0] : null;
}
export function makePeople(staff, saved) {
  const byId = Object.fromEntries((staff || []).map(s => [s.id, s]));
  const map = Object.fromEntries((saved || []).map(p => [p.sg_name, p]));
  const cache = {};
  return function who(sg) {
    if (cache[sg]) return cache[sg];
    const p = map[sg];
    const st = (p?.staff_id && byId[p.staff_id]) || matchStaff(sg, staff || []);
    // setter type saved under another Sales Gen name for the same person (e.g. "Donna Mc" → Donna Mcallister)
    const kindSaved = p?.kind || (st && (saved || []).find(x => x.kind && (x.staff_id === st.id || (!x.staff_id && matchStaff(x.sg_name, staff || [])?.id === st.id)))?.kind) || null;
    let roles = [];
    try { roles = st ? (Array.isArray(st.roles) ? st.roles : JSON.parse(st.roles || '[]')) : []; } catch (e) { roles = []; }
    if (st?.role && !roles.includes(st.role)) roles.unshift(st.role);
    const auto = roles.includes('Canvasser') && !roles.includes('Telemarketer') ? 'Canvasser'
      : roles.includes('Telemarketer') && !roles.includes('Canvasser') ? 'Telemarketer' : null;
    cache[sg] = { sg, staff: st || null, linked: !!p?.staff_id, kind: kindSaved || auto, kindSaved: !!p?.kind,
      name: st ? st.name : sg, nick: st?.nickname || '', photo: st?.photo ? (String(st.photo).startsWith('/') ? st.photo : `/uploads/profiles/${st.photo}`) : null };
    return cache[sg];
  };
}
const Who = ({ p, sub }) => (
  <div className="sc-who"><Avatar name={p.name} photo={p.photo} size={34} />
    <div><b>{p.name}</b>{(sub || (p.staff && p.name !== p.sg)) && <small>{sub || `Sales Gen: ${p.sg}`}</small>}</div></div>
);

const Meter = ({ value, team, color, max = 100 }) => (
  <div className="sc-meter" title={`${value ?? '—'}% · team ${team}%`}>
    <i style={{ width: `${Math.min(100, (value || 0) / max * 100)}%`, background: color }} />
    <u style={{ left: `calc(${Math.min(100, team / max * 100)}% - 1px)` }} aria-hidden />
  </div>
);
const sortRows = (rows, key) => [...rows].sort((a, b) => ORDER[a.v] - ORDER[b.v] || (a.v === 'good' ? b.s.revenue - a.s.revenue : (a.s[key] ?? 0) - (b.s[key] ?? 0)));

// ── Needs your attention: plain sentences across every job ──────────────────
export function Attention({ boards, all, onPick }) {
  const items = [];
  const bad = l => l.filter(r => r.v === 'bad');
  for (const s of bad(boards.set).sort((a, b) => b.s.wasted - a.s.wasted).slice(0, 2)) items.push({ tone: 'bad', who: s.n, kind: 'set',
    text: <><b>{s.p.name}</b> (setter): owners were home on only <b>{s.s.ownersHome}%</b> of the trips reps made to their appointments (team {boards.team.set.ownersHome}%).</>,
    sub: `${plural(s.s.wasted, 'wasted trip')} out of ${s.s.set} set · ${s.s.sold} sold` });
  for (const s of bad(boards.conf).slice(0, 1)) items.push({ tone: 'bad', who: s.n, kind: 'conf',
    text: <><b>{s.p.name}</b> (confirmer): after confirming, owners were home on only <b>{s.s.ownersHome}%</b> of trips (team {boards.team.conf.ownersHome}%).</>,
    sub: `${plural(s.s.wasted, 'wasted trip')} out of ${s.s.set} confirmed` });
  for (const r of bad(boards.rep).sort((a, b) => b.s.sat - a.s.sat).slice(0, 1)) items.push({ tone: 'bad', who: r.n, kind: 'rep',
    text: <><b>{r.p.name}</b> (rep) sat down with owners <b>{r.s.sat}</b> times and sold <b>{r.s.sold}</b> (team sells {boards.team.rep.sitSale}% of sit-downs).</>,
    sub: `${money(r.s.perSit)} per sit-down vs team ${money(boards.team.rep.perSit)}` });
  const unlucky = [...boards.rep].filter(r => r.s.wasted >= 5).sort((a, b) => b.s.wasted / b.s.trips - a.s.wasted / a.s.trips)[0];
  if (unlucky) items.push({ tone: 'watch', who: unlucky.n, kind: 'rep',
    text: <><b>{unlucky.p.name}</b> drove to <b>{unlucky.s.wasted}</b> appointments where no one sat down. That's the setters' problem, not theirs.</>,
    sub: `${pctT(unlucky.s.wasted, unlucky.s.trips)} of their trips. Click to see who set them.` });
  const bestRep = [...boards.rep].filter(r => r.s.sat >= 5).sort((a, b) => b.s.perSit - a.s.perSit)[0];
  if (bestRep) items.push({ tone: 'good', who: bestRep.n, kind: 'rep',
    text: <><b>{bestRep.p.name}</b> makes the most per sit-down: <b>{money(bestRep.s.perSit)}</b> (team {money(boards.team.rep.perSit)}).</>,
    sub: `${money(bestRep.s.revenue)} from ${plural(bestRep.s.sat, 'sit-down')}` });
  if (all.pend) items.push({ tone: 'watch', kind: 'pend',
    text: <><b>{all.pend}</b> appointments were dispatched but <b>no result was ever entered</b>.</>,
    sub: 'They are left out of every rate until a result is logged.' });
  if (!items.length) return null;
  return (
    <div className="sc-att">
      <h2>Needs your attention</h2>
      <div className="sc-items">
        {items.slice(0, 6).map((it, i) => { const t = VERDICT[it.tone]; return (
          <button key={i} type="button" className="sc-item" onClick={() => onPick(it)}>
            <span className="ic" style={{ background: t.bg, color: t.fg }} aria-hidden>{t.icon}</span>
            <div><p>{it.text}</p><small>{it.sub}</small></div>
          </button>); })}
      </div>
    </div>
  );
}

const Question = ({ title, text, num, numLabel }) => (
  <div className="sc-q"><div><h2>{title}</h2><p>{text}</p></div>
    {num != null && <div className="num"><b>{num}</b><span>{numLabel}</span></div>}</div>
);

// ── Sales reps ──────────────────────────────────────────────────────────────
export function RepBoard({ rows, team, onOpen }) {
  const fair = [...rows].filter(r => r.s.sold > 0).sort((a, b) => b.s.perSit - a.s.perSit);
  const none = [...rows].filter(r => !r.s.sold && r.s.sat > 0).sort((a, b) => b.s.sat - a.s.sat);
  const maxRev = Math.max(1, ...fair.map(r => r.s.revenue)), maxSit = Math.max(1, ...fair.map(r => r.s.perSit));
  return (
    <>
      <Question title="Sales reps: when they get in front of the owners, do they sell?"
        text="A rep can't sell to an empty house. No shows, no opps, NRAs and cancellations are the setter's or confirmer's problem, so reps are judged on what happens after they sit down with the owners."
        num={money(team.perSit)} numLabel="team revenue per sit-down" />
      <div className="ap-card">
        <h2 className="t">Fair comparison: total revenue vs revenue per sit-down</h2>
        <div className="hint">Big totals can just mean more appointments. The right-hand bar shows what each rep makes every time they actually sit down with owners. Sorted by that number.</div>
        <div className="sc-fair">
          <div className="h">Sales rep</div><div className="h tot">Total revenue</div><div className="h">Revenue per sit-down</div>
          {fair.map(r => (
            <FairRow key={r.n} r={r} maxRev={maxRev} maxSit={maxSit} onOpen={onOpen} />
          ))}
        </div>
        {none.length > 0 && <div className="hint" style={{ marginTop: 14, marginBottom: 0 }}><b style={{ color: '#13211B' }}>No sales yet:</b> {none.map((r, i) => <span key={r.n}>{i ? ', ' : ''}<button type="button" className="sc-link" onClick={() => onOpen(r.n, 'rep')}>{r.p.name}</button> ({plural(r.s.sat, 'sit-down')})</span>)}</div>}
      </div>
      <div className="ap-card">
        <h2 className="t">Sales rep scorecard</h2>
        <div className="sc-legend">
          <span><b style={{ color: '#13211B' }}>Sit-down → sale</b> = sold ÷ times they sat down with owners</span>
          <span><span className="sc-tick" /> team {team.sitSale}%</span>
          <span><b style={{ color: '#13211B' }}>Not their fault</b> = no show, no opp or NRA</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="sc-tbl">
            <thead><tr><th>Sales rep</th><th>Verdict</th><th className="r">Appts</th><th className="r">Not their fault<span className="sc-th2">no show / no opp / NRA</span></th><th className="r">Sat down</th><th>Sit-down → sale</th><th className="r">Sold</th><th className="r">Revenue</th><th className="r">$ per sit-down</th><th className="r ap-hide">In, no demo</th><th className="r ap-hide">No result</th></tr></thead>
            <tbody>{sortRows(rows, 'sitSale').map(r => (
              <tr key={r.n} className="click" onClick={() => onOpen(r.n, 'rep')}>
                <td><Who p={r.p} /></td>
                <td><Verdict v={r.v} /></td>
                <td className="r">{r.s.set}</td>
                <td className="r">{r.s.wasted || '—'}{r.s.wasted ? <span className="ap-sub"> {pctT(r.s.wasted, r.s.trips)}</span> : ''}</td>
                <td className="r"><b>{r.s.sat}</b></td>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="sc-big" style={{ minWidth: 40 }}>{r.s.sitSale == null ? '—' : r.s.sitSale + '%'}</span><Meter value={r.s.sitSale} team={team.sitSale} max={60} color={VERDICT[r.v].bar} /></div></td>
                <td className="r ap-good"><b>{r.s.sold || '—'}</b></td>
                <td className="r">{r.s.revenue ? money(r.s.revenue) : '—'}</td>
                <td className="r"><b>{r.s.sat ? money(r.s.perSit) : '—'}</b></td>
                <td className="r ap-hide">{r.s.nodemo || '—'}</td>
                <td className={'r ap-hide' + (r.s.pend >= 3 ? ' ap-bad' : '')}>{r.s.pend || '—'}</td>
              </tr>))}</tbody>
          </table>
        </div>
        <div className="hint" style={{ marginTop: 10, marginBottom: 0 }}>Shared appointments count for each rep, and the revenue is split between them. Click a rep to see every appointment and who set the ones that were wasted.</div>
      </div>
    </>
  );
}
const FairRow = ({ r, maxRev, maxSit, onOpen }) => (
  <>
    <button type="button" className="sc-mini-btn" style={{ border: 0, padding: 0, background: 'none', color: '#13211B', textAlign: 'left' }} onClick={() => onOpen(r.n, 'rep')}><Who p={r.p} sub={plural(r.s.sat, 'sit-down') + ` · ${r.s.set} appts`} /></button>
    <div className="sc-fbar tot"><i style={{ width: `${r.s.revenue / maxRev * 70}%`, background: '#A9BDB2' }} /><span>{money(r.s.revenue)}</span></div>
    <div className="sc-fbar"><i style={{ width: `${r.s.perSit / maxSit * 70}%`, background: '#0D3B2E' }} /><span><b>{money(r.s.perSit)}</b> <small>· {r.s.sold} sold</small></span></div>
  </>
);

// ── Setters (telemarketers / canvassers) ────────────────────────────────────
export function SetterBoard({ kind, rows, team, unsorted, onSort, onOpen }) {
  const word = kind === 'Canvasser' ? 'canvasser' : 'telemarketer';
  const s = rows.reduce((a, r) => ({ wasted: a.wasted + r.s.wasted, trips: a.trips + r.s.trips }), { wasted: 0, trips: 0 });
  return (
    <>
      <Question title={`${kind}s: are they booking real appointments?`}
        text={`Every appointment a ${word} books sends a rep out. If the owners aren't home, that's a wasted trip and a wasted evening for the rep. This board shows how often the owners were actually there.`}
        num={rows.length ? pctT(s.trips - s.wasted, s.trips) : null} numLabel={`owners home on ${word}s' appointments`} />
      {unsorted.length > 0 && (
        <div className="sc-unsorted">
          <h3>{plural(unsorted.length, 'setter')} not sorted yet</h3>
          <p>Sales Gen doesn't say whether a setter is a telemarketer or a canvasser. Pick once and it's remembered. (Adding the role on their Staff Profile does this automatically.)</p>
          <div className="sc-us-row">{unsorted.map(p => (
            <span key={p.sg} className="sc-us">{p.name}<button type="button" onClick={() => onSort(p.sg, 'Telemarketer')}>Telemarketer</button><button type="button" onClick={() => onSort(p.sg, 'Canvasser')}>Canvasser</button></span>))}</div>
        </div>
      )}
      {!rows.length ? <div className="sc-empty">No {word}s yet for these dates. {unsorted.length ? 'Sort the setters above and they will show up here.' : ''}</div> : (
        <div className="ap-card">
          <h2 className="t">{kind} scorecard</h2>
          <div className="sc-legend">
            <span><b style={{ color: '#13211B' }}>Owners home</b> = trips where the rep sat down with the owners ÷ all trips the rep made</span>
            <span><span className="sc-tick" /> team {team.ownersHome}%</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="sc-tbl">
              <thead><tr><th>{kind}</th><th>Verdict</th><th>Owners home</th><th className="r">Set</th><th className="r">Wasted trips<span className="sc-th2">no show / no opp / NRA</span></th><th className="r ap-hide">Cancelled</th><th className="r">Sold</th><th className="r">Revenue</th><th className="r">$ per appt set</th><th className="r ap-hide">No result</th></tr></thead>
              <tbody>{sortRows(rows, 'ownersHome').map(r => (
                <tr key={r.n} className="click" onClick={() => onOpen(r.n, 'set')}>
                  <td><Who p={r.p} /></td>
                  <td><Verdict v={r.v} /></td>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="sc-big" style={{ minWidth: 40 }}>{r.s.ownersHome == null ? '—' : r.s.ownersHome + '%'}</span><Meter value={r.s.ownersHome} team={team.ownersHome} color={VERDICT[r.v].bar} /></div></td>
                  <td className="r">{r.s.set}</td>
                  <td className={'r' + (r.s.wasted ? ' ap-bad' : '')}><b>{r.s.wasted || '—'}</b></td>
                  <td className="r ap-hide">{r.s.canc || '—'}</td>
                  <td className="r ap-good"><b>{r.s.sold || '—'}</b></td>
                  <td className="r">{r.s.revenue ? money(r.s.revenue) : '—'}</td>
                  <td className="r"><b>{r.s.revenue ? money(r.s.perAppt) : '—'}</b></td>
                  <td className="r ap-hide">{r.s.pend || '—'}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 10, marginBottom: 0 }}>Verdict: Good = within 5 points of the team, Watch = 5–15 below, Problem = more than 15 below. Fewer than 5 trips is too few to judge. When two setters share an appointment, both get it and the revenue is split.</div>
        </div>
      )}
    </>
  );
}

// ── Confirmers ──────────────────────────────────────────────────────────────
export function ConfirmerBoard({ rows, team, onOpen }) {
  return (
    <>
      <Question title="Confirmers: when they confirm, are the owners really there?"
        text="A confirmed appointment should mean the owners will be home. If a confirmer confirms 20 and 16 are no shows, the confirmation isn't working. Cancelling before the rep drives out is good: that's a trip saved."
        num={team.ownersHome == null ? null : team.ownersHome + '%'} numLabel="owners home after confirming" />
      {!rows.length ? <div className="sc-empty">No confirmers on these appointments.</div> : (
        <div className="ap-card">
          <h2 className="t">Confirmer scorecard</h2>
          <div className="sc-legend">
            <span><b style={{ color: '#13211B' }}>Owners home</b> = of the trips reps made on appointments they confirmed, how often the owners sat down</span>
            <span><span className="sc-tick" /> team {team.ownersHome}%</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="sc-tbl">
              <thead><tr><th>Confirmer</th><th>Verdict</th><th className="r">Confirmed</th><th>Owners home</th><th className="r">No show after confirming<span className="sc-th2">no show / no opp / NRA</span></th><th className="r">Trips saved<span className="sc-th2">cancelled before the trip</span></th><th className="r">Sold</th><th className="r ap-hide">No result</th></tr></thead>
              <tbody>{sortRows(rows, 'ownersHome').map(r => (
                <tr key={r.n} className="click" onClick={() => onOpen(r.n, 'conf')}>
                  <td><Who p={r.p} /></td>
                  <td><Verdict v={r.v} /></td>
                  <td className="r">{r.s.set}</td>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="sc-big" style={{ minWidth: 40 }}>{r.s.ownersHome == null ? '—' : r.s.ownersHome + '%'}</span><Meter value={r.s.ownersHome} team={team.ownersHome} color={VERDICT[r.v].bar} /></div></td>
                  <td className={'r' + (r.s.wasted ? ' ap-bad' : '')}><b>{r.s.wasted || '—'}</b>{r.s.wasted ? <span className="ap-sub"> of {r.s.trips} trips · {pctT(r.s.wasted, r.s.trips)}</span> : ''}</td>
                  <td className="r">{r.s.canc || '—'}</td>
                  <td className="r ap-good"><b>{r.s.sold || '—'}</b></td>
                  <td className="r ap-hide">{r.s.pend || '—'}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ── Crews (vans with a crew chief) — appointments booked in CPR Live ───────────
export function CrewBoard({ rows, team, onOpen }) {
  return (
    <>
      <Question title="Crews: which vans bring in real appointments?"
        text="Each crew is a van with a crew chief and canvassers. This shows how often the owners were home on the appointments each crew set, and what those appointments turned into."
        num={team.ownersHome == null ? null : team.ownersHome + '%'} numLabel="owners home, all setters" />
      <div className="ap-card">
        <h2 className="t">Crew scorecard</h2>
        <div className="sc-legend"><span><b style={{ color: '#13211B' }}>Owners home</b> = trips where the rep sat down with the owners ÷ all trips the rep made</span><span><span className="sc-tick" /> team {team.ownersHome}%</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="sc-tbl">
            <thead><tr><th>Crew</th><th>Verdict</th><th>Owners home</th><th className="r">Set</th><th className="r">Wasted trips</th><th className="r ap-hide">Cancelled</th><th className="r">Sold</th><th className="r">Revenue</th><th className="r">$ per appt set</th></tr></thead>
            <tbody>{sortRows(rows, 'ownersHome').map(r => (
              <tr key={r.n} className="click" onClick={() => onOpen(r.n, 'crew')}>
                <td><b>{r.n}</b></td><td><Verdict v={r.v} /></td>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="sc-big" style={{ minWidth: 40 }}>{r.s.ownersHome == null ? '—' : r.s.ownersHome + '%'}</span><Meter value={r.s.ownersHome} team={team.ownersHome} color={VERDICT[r.v].bar} /></div></td>
                <td className="r">{r.s.set}</td><td className={'r' + (r.s.wasted ? ' ap-bad' : '')}><b>{r.s.wasted || '—'}</b></td><td className="r ap-hide">{r.s.canc || '—'}</td>
                <td className="r ap-good"><b>{r.s.sold || '—'}</b></td><td className="r">{r.s.revenue ? money(r.s.revenue) : '—'}</td><td className="r"><b>{r.s.revenue ? money(r.s.perAppt) : '—'}</b></td>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── One person, everything about them ──────────────────────────────────────
const ROLE_NAME = { rep: 'Sales rep', set: 'Setter', conf: 'Confirmer', crew: 'Crew' };
export function PersonPanel({ p, kind, list, team, out, who, onClose }) {
  useEffect(() => {
    const k = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const s = statsOf(list, kind === 'rep' ? 'reps' : kind === 'set' ? 'setters' : null);
  const v = kind === 'rep' ? repVerdict(s, team.rep) : homeVerdict(s, kind === 'conf' ? team.conf : team.set);
  const name = p.name;
  // Wasted trips grouped by who set / ran them
  const otherField = kind === 'rep' || kind === 'crew' ? 'setters' : 'reps';
  const partners = {};
  for (const a of list) for (const q of a[otherField]) { partners[q] = partners[q] || []; partners[q].push(a); }
  const partnerRows = Object.entries(partners).map(([n, l]) => ({ n, s: statsOf(l) })).sort((a, b) => b.s.wasted - a.s.wasted || b.s.set - a.s.set);
  const say = kind === 'rep'
    ? (s.set ? `${name} was given ${plural(s.set, 'appointment')}. ${s.wasted ? `${s.wasted} of the trips were wasted before the door (no show, no opp or NRA), which is not the rep's fault. ` : ''}${s.canc ? `${s.canc} cancelled before the trip. ` : ''}They sat down with the owners ${plural(s.sat, 'time')} and sold ${s.sold}${s.sat ? ` (${s.sitSale}%, team ${team.rep.sitSale}%)` : ''}, bringing in ${money(s.revenue)}${s.sat ? `, or ${money(s.perSit)} per sit-down (team ${money(team.rep.perSit)})` : ''}.` : '')
    : kind === 'set' || kind === 'crew'
      ? (s.trips ? `When a rep went to one of ${name}'s appointments, the owners were home ${s.ownersHome}% of the time (team ${team.set.ownersHome}%). ${plural(s.wasted, 'trip')} ${s.wasted === 1 ? 'was' : 'were'} wasted on no shows, no opps or NRAs. ${s.sold ? `${s.sold} turned into sales worth ${money(s.revenue)}, or ${money(s.perAppt)} per appointment set.` : 'None have turned into a sale yet.'}` : 'Not enough appointments with a result to judge yet.')
      : (s.trips ? `${name} confirmed ${plural(s.set, 'appointment')}. When the rep went out, the owners were home ${s.ownersHome}% of the time (team ${team.conf.ownersHome}%), so ${plural(s.wasted, 'trip')} ${s.wasted === 1 ? 'was' : 'were'} wasted after confirming. ${s.canc ? `${s.canc} ${s.canc === 1 ? 'was' : 'were'} cancelled before the trip, saving the rep the drive.` : ''}` : 'Not enough appointments with a result to judge yet.');
  const steps = [
    { l: kind === 'set' ? 'Appointments set' : kind === 'conf' ? 'Confirmed' : 'Appointments given', n: s.set, c: '#5E6E66' },
    { l: 'Rep drove out', n: s.trips, c: '#2a78d6' },
    { l: 'Owners sat down', n: s.sat, c: '#0D3B2E' },
    { l: 'Sold', n: s.sold, c: '#1f7a4a' },
  ];
  const max = Math.max(1, s.set);
  return (
    <div className="sc-panel-wrap" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="sc-panel" role="dialog" aria-label={name}>
        <header>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <Avatar name={name} photo={p.photo} size={56} />
            <div><div className="role">{ROLE_NAME[kind]}{kind === 'set' && p.kind ? ` · ${p.kind}` : ''}{p.staff && p.sg !== name ? ` · Sales Gen: ${p.sg}` : ''}</div><h2>{name}</h2><div style={{ marginTop: 6 }}><Verdict v={v} /></div></div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="sc-pb">
          <div className="sc-kpis">
            <div className="sc-kpi"><b>{s.set}</b><span>appointments</span></div>
            {kind === 'rep'
              ? <div className="sc-kpi"><b>{s.sitSale == null ? '—' : s.sitSale + '%'}</b><span>sit-downs sold</span></div>
              : <div className="sc-kpi"><b>{s.ownersHome == null ? '—' : s.ownersHome + '%'}</b><span>owners home</span></div>}
            <div className="sc-kpi"><b>{kind === 'rep' ? money(s.perSit) : s.sold}</b><span>{kind === 'rep' ? 'per sit-down' : `sold · ${money(s.revenue)}`}</span></div>
            <div className="sc-kpi"><b>{s.wasted}</b><span>{kind === 'rep' ? 'not their fault' : 'wasted trips'}</span></div>
          </div>
          <div className="sc-say">{say}</div>
          <div className="sc-funnel">
            <h3>From appointment to sale</h3>
            {steps.map(st => <div key={st.l} className="sc-fn"><span>{st.l}</span><i style={{ width: `${Math.max(1, st.n / max * 100)}%`, background: st.c }} /><span>{st.n}</span></div>)}
            <div className="sc-fn-note">Lost on the way: {s.canc} cancelled before the trip · {s.wasted} no show / no opp / NRA · {s.demo} pitched, no sale · {s.nodemo} got in, no demo{s.pend ? ` · ${s.pend} no result entered` : ''}</div>
          </div>
          {partnerRows.length > 0 && (
            <div className="sc-grp">
              <h3>{kind === 'rep' ? 'Who set their appointments' : kind === 'crew' ? 'Canvassers in this crew' : 'Reps who ran these appointments'}</h3>
              <table><tbody>{partnerRows.map(q => (
                <tr key={q.n}><td><b>{who(q.n).name}</b></td><td>{plural(q.s.set, 'appt')}</td><td>{q.s.sold} sold</td><td className={q.s.wasted ? 'ap-bad' : ''}>{q.s.wasted} wasted</td><td>{q.s.trips ? `${q.s.ownersHome}% owners home` : ''}</td></tr>))}</tbody></table>
            </div>
          )}
          {out.filter(o => list.some(a => a.out === o.k)).map(o => {
            const l = list.filter(a => a.out === o.k);
            return (
              <div key={o.k} className="sc-grp">
                <h3><span className={'ap-sw' + (o.k === 'pend' ? ' pend' : '')} style={o.k === 'pend' ? undefined : { background: o.color }} />{o.label} · {l.length}</h3>
                <table><tbody>{l.map(a => (
                  <tr key={a.id}><td style={{ whiteSpace: 'nowrap' }}>{+a.appt_date.slice(5, 7)}/{+a.appt_date.slice(8)}<br /><span className="ap-sub">{a.appt_time}</span></td>
                    <td><b>{a.customer}</b><br /><span className="ap-sub">{a.city}</span></td>
                    <td>{kind === 'rep' ? `Set by ${a.setter || '—'}` : `Rep: ${a.rep || '—'}`}<br /><span className="ap-sub">{a.product}{kind !== 'conf' && a.confirmer ? ` · conf. ${a.confirmer}` : ''}</span></td>
                    <td>{a.out === 'sold' ? <b>{money(a.amount)}</b> : <span className="ap-sub">{a.disp}</span>}</td>
                    <td className="ap-sub" style={{ maxWidth: 220 }}>{a.notes}</td></tr>))}</tbody></table>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}

// ── Who's who: link Sales Gen names to staff profiles ──────────────────────
export function WhosWho({ names, who, staff, onSave, onClose }) {
  const [busy, setBusy] = useState('');
  useEffect(() => {
    const k = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const save = async (sg, staffId, kind) => { setBusy(sg); try { await onSave(sg, staffId, kind); } finally { setBusy(''); } };
  const active = [...(staff || [])].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  return (
    <div className="sc-panel-wrap" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="sc-panel" role="dialog" aria-label="Who's who">
        <header><div><div className="role">Sales Gen names</div><h2>Who's who</h2></div><button type="button" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="sc-pb">
          <div className="sc-say">Sales Gen prints short names like "Donna Mc". Link each one to a Staff Profile so the boards show their full name and photo. Names that clearly match are linked automatically. For setters, also say if they're a telemarketer or a canvasser.</div>
          <div className="sc-grp"><table className="sc-ww"><thead><tr><th style={{ textAlign: 'left', padding: 8 }}>Sales Gen name</th><th style={{ textAlign: 'left', padding: 8 }}>Shows up as</th><th style={{ textAlign: 'left', padding: 8 }}>Staff profile</th><th style={{ textAlign: 'left', padding: 8 }}>Setter type</th></tr></thead>
            <tbody>{names.map(x => { const p = who(x.n); return (
              <tr key={x.n} style={{ opacity: busy === x.n ? 0.5 : 1 }}>
                <td><b>{x.n}</b></td>
                <td className="ap-sub">{x.roles.join(', ')}</td>
                <td><select value={p.staff?.id || ''} onChange={e => save(x.n, e.target.value || null, p.kindSaved ? p.kind : null)}>
                  <option value="">{p.staff ? '—' : 'Not linked'}</option>
                  {active.map(s => <option key={s.id} value={s.id}>{s.name}{s.nickname ? ` · ${s.nickname}` : ''}{s.status && s.status !== 'Active' ? ` (${s.status})` : ''}</option>)}
                </select>{p.staff && !p.linked && <span className="ap-sub"> auto</span>}</td>
                <td>{x.roles.includes('Setter') ? (
                  <select value={p.kindSaved ? p.kind : ''} onChange={e => save(x.n, p.linked ? p.staff.id : null, e.target.value || null)}>
                    <option value="">{p.kind && !p.kindSaved ? `${p.kind} (from profile)` : 'Not set'}</option>
                    <option>Telemarketer</option><option>Canvasser</option>
                  </select>) : <span className="ap-sub">—</span>}</td>
              </tr>); })}</tbody></table></div>
        </div>
      </aside>
    </div>
  );
}
